<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed } from 'vue'
import { useContentHelper } from '~/composables/useContentHelper'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useDescriptor from '~/composables/useDescriptor'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import ResourceCardMenu from '~/components/cards/buttons/ResourceCardMenu.vue'
import LearningPathLink from '~/components/utils/LearningPathLink.vue'

const props = defineProps<{
  resource: BaseItem
  isAnchor?: boolean
}>()

const { getTitle, isLearningPath } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()
const { getDescriptor } = useDescriptor()

const title = computed(() => getTitle(props.resource))
const link = computed(() => buildResourceUrl(props.resource))
const label = getDescriptor(props.resource)

const attributes = computed(() => isLearningPath(props.resource)
  ? {
    resource: props.resource
  }
  : {
    link: link.value,
    isAnchor: props.isAnchor
  }
)
</script>

<template>
  <div class="relative flex h-full w-full flex-col gap-2 border border-[var(--card-border)] bg-[var(--card-bg)] px-6 py-4 text-xl font-bold text-[var(--card-text)] ring-inset transition duration-100 has-[a:hover]:bg-[var(--card-hover)] has-[a:focus-visible]:ring has-[a:hover]:ring has-[a:hover]:ring-[var(--card-border)]">
    <div
      :id="`card-${resource.locationId}`"
      class="contents"
    >
      <div class="text-xs uppercase text-[var(--card-accent)]">
        {{ label }}
      </div>

      <div class="break-words text-lg font-medium">
        {{ title }}
      </div>
    </div>

    <component
      :is="isLearningPath(resource) ? LearningPathLink : LinkOrAnchor"
      :aria-labeledby="`card-${resource.locationId}`"
      class="absolute inset-0 select-none opacity-0"
      v-bind="attributes"
    />

    <ResourceCardMenu
      class="relative mt-auto self-start"
      :resource="resource"
      button-variant="border"
      style="--ks-border: transparent"
    />
  </div>
</template>
